import * as React from 'react';
import { TableContainer, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { downloadErrors, errorActions } from './ErrorsActions';
import { Table } from '@acoer/ac-js-lib-charts';
import Loader from '../../../Shared/Loader';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';

const ErrorsTable = ({ fileName }): JSX.Element => {
    const dispatch = useDispatch();
    const errorState = useSelector((state: any) => state.errors);
    const errorsLoading = useSelector((state: any) => state.errors.loading);
    const errors = errorState?.errors;
    const fileImportStatus = useSelector(
        (state: any) => state.workspace.initiatedFileImport
    );
    const fileImportStatusId = fileImportStatus?.id;
    const errorItems = errors?.items;
    const [pageLimit, setPageLimit] = useState<number>(10);
    const [offset, setOffset] = useState<number>(0);
    const [sortField, setSortField] = useState<string>('lastUpdated');
    const [sortDirection, setSortDirection] = useState<string>('DESC');
    const [pageNumber, setPageNumber] = useState<number>(0);
    const theme = useTheme();

    const handleDownload = () => {
        dispatch(downloadErrors(fileImportStatusId, fileName));
    };

    useEffect(() => {
        if (fileImportStatusId) {
            handleFetch('initial');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileImportStatusId]);

    const handleFetch = (type: string) => {
        dispatch(
            errorActions.getErrors(
                fileImportStatusId,
                pageLimit,
                offset,
                sortField,
                sortDirection,
                type
            )
        );
    };

    const handleTableChange = (action: any, tableState: any) => {
        if (action !== 'propsUpdate') {
            const limit = tableState.rowsPerPage;
            const skip = tableState.rowsPerPage * tableState.page;
            const field = tableState.sortOrder.name || sortField;
            const direction =
                (tableState.sortOrder.direction &&
                    tableState.sortOrder.direction.toUpperCase()) ||
                (sortDirection && sortDirection.toUpperCase());
            const page = tableState.page;

            dispatch(
                errorActions.getErrors(
                    fileImportStatusId,
                    limit,
                    skip,
                    field,
                    direction
                )
            );
            setOffset(skip);
            setPageLimit(limit);
            setSortField(field);
            setSortDirection(direction);
            setPageNumber(page);
        }
    };

    const loadingComponent = (
        <div
            style={{
                position: 'absolute',
                zIndex: 110,
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: `#fff80`,
            }}
        >
            <Loader size={60} color={'#60CEBD'} />
        </div>
    );

    return (
        <>
            <div
                id="FileImportDiv"
                style={{ position: 'relative', cursor: 'auto' }}
            >
                {errorsLoading && loadingComponent}
                <TableContainer style={{ padding: '20px 20px' }}>
                    <Table
                        onTableChange={handleTableChange}
                        pageLimit={pageLimit}
                        pageNumber={pageNumber}
                        chartTitle={
                            <Typography
                                marginTop={-4}
                                marginLeft={1}
                                variant="h6"
                            >
                                Import Errors for "{fileName}"
                            </Typography>
                        }
                        downloadableCsv={true}
                        onDownloadCsv={handleDownload}
                        theme={theme}
                        data={errorItems || []}
                        count={errors?.total || 0}
                        page={pageNumber}
                        sortOrder={{
                            direction: sortDirection.toLowerCase(),
                            name: sortField,
                        }}
                        serverSide
                        rowsPerPageOptions={[10, 25, 50]}
                        searchable={true}
                        pagination
                        rowsPerPage={pageLimit}
                        columns={[
                            {
                                name: 'type',
                                label: 'Error Type',
                                options: {
                                    setCellProps: () => ({
                                        style: {
                                            width: 20,
                                        },
                                    }),
                                    customBodyRender: (value: string) => {
                                        if (value === 'WARNING') {
                                            return (
                                                <Grid
                                                    container
                                                    justifyContent={'center'}
                                                >
                                                    <Tooltip title={'Warning'}>
                                                        <Grid item>
                                                            <WarningAmberIcon
                                                                color={
                                                                    'warning'
                                                                }
                                                            />
                                                        </Grid>
                                                    </Tooltip>
                                                </Grid>
                                            );
                                        } else if (value === 'ERROR') {
                                            return (
                                                <Grid
                                                    container
                                                    justifyContent={'center'}
                                                >
                                                    <Tooltip title={'Error'}>
                                                        <Grid item>
                                                            <ErrorOutlineIcon
                                                                color={'error'}
                                                            />
                                                        </Grid>
                                                    </Tooltip>
                                                </Grid>
                                            );
                                        } else {
                                            return '---';
                                        }
                                    },
                                    filter: true,
                                    sort: true,
                                },
                            },
                            {
                                name: 'caseId',
                                label: 'Case ID',
                                options: {
                                    customBodyRender: (value: string) => {
                                        if (value) {
                                            return value;
                                        } else {
                                            return 'No case ID available';
                                        }
                                    },
                                    filter: true,
                                    sort: true,
                                },
                            },
                            {
                                name: 'fieldName',
                                label: 'Field Name',
                                options: {
                                    filter: true,
                                    sort: true,
                                },
                            },
                            {
                                name: 'errorMessage',
                                label: 'Error Message',
                                options: {
                                    filter: true,
                                    sort: true,
                                },
                            },
                        ]}
                    />
                </TableContainer>
            </div>
        </>
    );
};

export default ErrorsTable;
